/* oxlint-disable no-inner-declarations */
import { UTCDate } from '@date-fns/utc';
import * as rq from '@tanstack/react-query';
import { format } from 'date-fns';

import { useDexFetchClient } from '@/api/looker/utils/use-dex-fetch-client';
import {
  type ChartData,
  ChartDataSchema,
} from '@/data-explorer-v2/entities/chart-data';
import { ChartType } from '@/data-explorer-v2/entities/chart-type';
import type { RequestFilters } from '@/data-explorer-v2/entities/filters';
import type { Node } from '@/data-explorer-v2/entities/node';
import { useMe } from '@/hooks/use-me';

const categoryFromChartType: Record<ChartType, string> = {
  [ChartType.CELL_VOLTAGE]: 'cell_voltage_chart',
  [ChartType.CURRENT_AC]: 'current_ac_chart',
  [ChartType.CURRENT]: 'current_chart',
  [ChartType.ENERGY]: 'energy_chart',
  [ChartType.FREQUENCY]: 'frequency_chart',
  [ChartType.HS_CONTROL_AGC]: 'hs_control_agc_chart',
  [ChartType.POWER_FACTOR]: 'power_factor_chart',
  [ChartType.POWER]: 'power_chart',
  [ChartType.RACKS_ONLINE]: 'racks_online_chart',
  [ChartType.SOC]: 'soc_chart',
  [ChartType.TEMPERATURE]: 'temperature_chart',
  [ChartType.UNITS_ONLINE]: 'units_online_chart',
  [ChartType.VOLTAGE_AC]: 'voltage_ac_chart',
  [ChartType.VOLTAGE]: 'voltage_chart',
};

/**
 * Validates and safely creates a ChartData object from potentially malformed backend data
 */
function createSafeChartData(column: string, data: unknown): ChartData | null {
  try {
    const candidateData = { column, data };
    return ChartDataSchema.parse(candidateData);
  } catch (error) {
    console.warn(
      `Invalid chart data format for column "${column}":`,
      error,
      'Raw data:',
      data,
    );
    return null;
  }
}

/**
 * Validates that data is an array of number tuples as expected by Highcharts
 */
function isValidHighchartsData(data: unknown): data is [number, number][] {
  return (
    Array.isArray(data) &&
    data.every(
      (point) =>
        Array.isArray(point) &&
        point.length === 2 &&
        typeof point[0] === 'number' &&
        typeof point[1] === 'number',
    )
  );
}

export namespace GetChartData {
  export const key = (input: Input) => [
    'data-explorer',
    'get-chart-data',
    input.node.id,
    input.from,
    input.to,
    input.filterNoise,
    Object.fromEntries(input.chartTypes.map((chartType) => [chartType, true])),
  ];

  export type QueryParams = {
    enabled?: boolean;
    retry?: number | boolean;
  };

  export type Input = {
    node: Node;
    chartTypes: ChartType[];
    from: Date;
    to: Date;
    filterNoise: boolean;
  };

  export type Output = Partial<Record<ChartType, ChartData[]>>;

  export type QueryInput = RequestFilters | undefined;
  export type QueryOutput = {
    node: Node;
    query: rq.UseBaseQueryResult<Output>;
  }[];

  type FetchColumnsResult = Record<string, string[]>;
  type FetchSensorsResult = Record<
    string,
    Record<string, [number, number][] | undefined> | undefined
  >;

  export function useQuery(input: QueryInput, opts?: QueryParams): QueryOutput {
    const inputs: Input[] = input
      ? input.nodes.map<Input>((node) => ({
          node,
          chartTypes: input.chartTypes,
          filterNoise: input.filterNoise,
          from: input.from,
          to: input.to,
        }))
      : [];

    const queries = useQueries(inputs, {
      enabled: Boolean(input),
      ...opts,
    });

    return inputs.map(({ node }, index) => ({ node, query: queries[index] }));
  }

  export function useQueries(
    inputs: Input[],
    { retry, enabled }: QueryParams = {},
  ) {
    const fetchClient = useDexFetchClient();
    const me = useMe();

    return rq.useQueries<Output[]>({
      queries: inputs.map((input) => ({
        enabled,
        retry,
        queryKey: key(input),
        async queryFn({ signal }) {
          const columnCategories = input.chartTypes.map(
            (chartType) => categoryFromChartType[chartType],
          );

          const columnsResult = await fetchClient<FetchColumnsResult>({
            path: `/v2/columns/${me.tenant}/${input.node.level}/${input.node.id}`,
            params: {
              ...(input.node.source !== '' && {
                'data-source': input.node.source,
              }),
            },
            signal,
          });

          // Early return if no columns available - skip /sensors call
          if (!columnsResult || Object.keys(columnsResult).length === 0) {
            return {} as Output;
          }

          const columnNames: string[] = Object.entries(columnsResult)
            .filter(([category]) => columnCategories.includes(category))
            .flatMap(([, n]) => n);

          // Early return if no columns match the requested chart types
          if (columnNames.length === 0) {
            return {} as Output;
          }

          const sensorsResult = await fetchClient<FetchSensorsResult>({
            path: `/v2/sensors/${me.tenant}/${input.node.level}/${input.node.id}`,
            params: {
              ...(input.node.source !== '' && {
                'data-source': input.node.source,
              }),
              'column-names': columnNames,
              'start-date': format(
                new UTCDate(input.from),
                "yyyy-MM-dd'T'HH:mm:ss",
              ),
              'end-date': format(
                new UTCDate(input.to),
                "yyyy-MM-dd'T'HH:mm:ss",
              ),
              'max-points': 500,
              'noise-filter': input.filterNoise ? 'True' : 'False',
            },
            signal,
          });

          // Only include chart types that have actual data
          const output: Output = {} as Output;

          for (const chartType of Object.values(ChartType)) {
            const categoryName = categoryFromChartType[chartType];
            const columns = sensorsResult[categoryName];

            if (!columns || typeof columns !== 'object') {
              continue;
            }

            const validChartData: ChartData[] = [];
            let malformedDataCount = 0;

            for (const [column, rawData] of Object.entries(columns)) {
              // Explicitly type the data to avoid TypeScript inference issues
              const data: unknown = rawData;

              // Skip null/undefined data
              if (data == null) {
                continue;
              }

              // Skip empty arrays
              if (Array.isArray(data) && data.length === 0) {
                continue;
              }

              // Validate data format before creating ChartData
              if (!isValidHighchartsData(data)) {
                console.warn(
                  `Skipping malformed data for ${chartType}/${column}:`,
                  'Expected array of [number, number] tuples, got:',
                  typeof data,
                  Array.isArray(data)
                    ? `Array with ${data.length} items`
                    : data,
                );
                malformedDataCount++;
                continue;
              }

              // Create validated ChartData object
              const safeChartData = createSafeChartData(column, data);
              if (safeChartData) {
                validChartData.push(safeChartData);
              } else {
                malformedDataCount++;
              }
            }

            // Log summary of data quality
            if (malformedDataCount > 0) {
              console.warn(
                `Chart data validation summary for ${chartType}:`,
                `${validChartData.length} valid columns,`,
                `${malformedDataCount} malformed columns skipped`,
              );
            }

            // Include chart type in output if it was in the sensor response
            // (even with empty data) - this allows the UI to show "no data" message
            // for charts that were requested but have no valid data
            output[chartType] = validChartData;
          }

          return output;
        },
      })),
    }) as rq.UseQueryResult<Output>[];
  }
}
